<!--
Style dans _article-live.css /!/ Ne pas utiliser tailwind sur html
car sinon le style du placeholder lors chargement (templates/themes/standard/include/homepage_live.html.twig) ne sera pas mise à jour
-->
<template>
    <div class="live-article-container relative">
        <div v-if="mainArticle.title" class="live-main-article">
            <div v-if="mainArticle.image" class="image-wrapper">
                <img :src="mainArticle.image" :alt="mainArticle.title">
            </div>
            <div class="main-content">
                <div class="live-badge-container">
                    <span class="live-badge-dot">
                        <span class="live-badge-dot-ping"></span>
                        <span class="live-badge-dot-static"></span>
                    </span>
                    <span class="live-badge-text">En direct</span>
                </div>
                <a class="link-after" :href="mainArticle.url">
                    <h2 class="live-title">{{ mainArticle.title }}</h2>
                </a>
                <p v-if="mainArticle.lead" class="live-lead">{{ mainArticle.lead }}</p>
                <CqUpdate :timestamp="lastUpdateTimestamp"></CqUpdate>
            </div>
        </div>

        <div class="timeline">
            <transition-group name="stack" tag="ul">
                <li v-for="paragraph in sortedParagraphs" :key="paragraph.identifier" class="timeline-item">
                    <a class="z-100 flex items-start gap-2 " :href="`${mainArticle.url}#paragraph-live-${paragraph.identifier}`">
                        <span class="time">{{ formatTime(paragraph.event_time) }}</span>
                        <span :class="['title-paragraph-live', { 'highlight': paragraph.highlight }]">
                            {{ paragraph.title }}
                        </span>
                    </a>
                </li>
            </transition-group>
        </div>
    </div>
</template>

<script>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import CqUpdate from './CqUpdate.vue';

export default {
    name: 'HomepageLive',
    components: {
        CqUpdate
    },
    props: {
        initialArticle: {
            type: Object,
            default: () => ({})
        },
        initialParagraphs: {
            type: Array,
            default: () => []
        }
    },
    setup(props) {
        const mainArticle = ref(props.initialArticle);
        const paragraphs = ref(props.initialParagraphs);

        const sortedParagraphs = computed(() => {
            return [...paragraphs.value].sort((a, b) => {
                return new Date(b.event_time) - new Date(a.event_time);
            });
        });
        // Calcule l'heure de l'événement le plus récent connu.
        // Retourne `null` si aucun paragraphe n'est encore chargé.
        const lastEventTime = computed(() => {
            if (sortedParagraphs.value.length === 0) {
                return null;
            }
            // Le premier élément de la liste triée est le plus récent.
            return sortedParagraphs.value[0].event_time;
        });

        const formatTime = (datetime) => {
            if (!datetime) return '';
            const date = new Date(datetime);
            return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
        };

        const fetchData = async () => {
            try {
                let apiUrl = '/api/homepage-live';

                // Ajoute le paramètre '?since=' pour ne récupérer que les mises à jour
                if (lastEventTime.value) {
                    apiUrl += `?since=${encodeURIComponent(lastEventTime.value)}`;
                }

                const response = await fetch(apiUrl);
                if (!response.ok) {
                    throw new Error(`Erreur HTTP: ${response.status}`);
                }
                const data = await response.json();

                // Met à jour l'article principal (uniquement si nécessaire).
                // Au premier chargement, `mainArticle.value` sera rempli.
                if (data.mainArticle) {
                    mainArticle.value = data.mainArticle;
                }

                // Ajoute les nouveaux paragraphes à notre liste réactive.
                if (data.newParagraphs && data.newParagraphs.length > 0) {
                    data.newParagraphs.forEach(newParagraph => {
                        // Vérifie que le paragraphe n'existe pas déjà pour éviter les doublons.
                        if (!paragraphs.value.some(p => p.identifier === newParagraph.identifier)) {
                            paragraphs.value.push(newParagraph);
                        }
                    });
                }
            } catch (error) {
                console.error('Erreur lors de la récupération des mises à jour:', error);
            }
        };

        let pollingIntervalId;

        onMounted(() => {
            fetchData();
            // Démarrer le polling pour vérifier les nouvelles données toutes les N secondes.
            pollingIntervalId = setInterval(fetchData, 60000); // 60000 ms = 60 secondes
        });

        onUnmounted(() => {
            clearInterval(pollingIntervalId);
        });

        const lastUpdateTimestamp = computed(() => {
            if (lastEventTime.value) {
                return Math.floor(new Date(lastEventTime.value).getTime() / 1000);
            }
            if (mainArticle.value.updated_at) {
                return Math.floor(new Date(mainArticle.value.updated_at).getTime() / 1000);
            }
            return 0;
        });

        // Seules les valeurs retournées ici sont utilisables dans le <template>.
        return {
            mainArticle,
            sortedParagraphs,
            formatTime,
            lastUpdateTimestamp
        };
    }
}
</script>

<style scoped>
    .stack-enter-active {
        transition: all 0.6s cubic-bezier(0.25, 0.8, 0.25, 1);
    }
    .stack-enter-from {
        opacity: 0;
        transform: translateY(-20px);
    }
    .stack-enter-to {
        opacity: 1;
        transform: translateY(0);
    }

</style>
